import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button } from '@structural-climbing/ui'
import { useGymContext } from '../../contexts/GymContext'
import { GymMapPreviewModal } from './GymMapPreviewModal'

const ACCEPTED = 'application/pdf,image/png,image/jpeg,image/webp'
const MAX_BYTES = 10 * 1024 * 1024

export function GymMapUpload() {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const ctx = useGymContext()
  const inputRef = useRef<HTMLInputElement>(null)

  const [mapUrl, setMapUrl] = useState(ctx.mapUrl)
  const [mapFileType, setMapFileType] = useState(ctx.mapFileType)
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)

  async function handleFile(file: File) {
    setError(null)
    if (file.size > MAX_BYTES) {
      setError(t('routes.gymMap.errorSize'))
      return
    }
    setUploading(true)
    try {
      const token = await getAccessTokenSilently()
      const body = new FormData()
      body.append('file', file)
      const res = await fetch(`${import.meta.env.VITE_API_URL as string}/gym-map`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
      })
      if (!res.ok) {
        const { error: msg } = (await res.json()) as { error?: string }
        setError(msg ?? t('common.uploadFailed'))
        return
      }
      const { url, file_type } = (await res.json()) as { url: string; file_type: 'pdf' | 'image' }
      setMapUrl(url)
      setMapFileType(file_type)
    } catch {
      setError(t('common.uploadFailedRetry'))
    } finally {
      setUploading(false)
    }
  }

  async function handleDelete() {
    setDeleting(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${import.meta.env.VITE_API_URL as string}/gym-map`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) {
        setMapUrl(null)
        setMapFileType(null)
      }
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-divider bg-content1 p-4">
      <p className="text-sm font-semibold text-foreground/60">{t('routes.gymMap.title')}</p>

      {mapUrl ? (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            aria-label={t('routes.gymMap.title')}
            className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-divider hover:opacity-80"
          >
            {mapFileType === 'image' ? (
              <img src={mapUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-content2 text-xs text-foreground/40">
                PDF
              </div>
            )}
          </button>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="flat"
              isLoading={uploading}
              isDisabled={uploading || deleting}
              onClick={() => inputRef.current?.click()}
            >
              {t('routes.gymMap.replaceButton')}
            </Button>
            <Button
              size="sm"
              variant="flat"
              color="danger"
              isLoading={deleting}
              isDisabled={uploading || deleting}
              onClick={() => void handleDelete()}
            >
              {t('routes.gymMap.deleteButton')}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          size="sm"
          variant="flat"
          color="primary"
          isLoading={uploading}
          isDisabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="w-fit"
        >
          {t('routes.gymMap.uploadLabel')}
        </Button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void handleFile(file)
          e.target.value = ''
        }}
      />
      <p className="text-xs text-foreground/40">{t('routes.gymMap.hint')}</p>
      {error && <p className="text-xs text-danger">{error}</p>}

      {mapUrl && mapFileType && (
        <GymMapPreviewModal
          mapUrl={mapUrl}
          mapFileType={mapFileType}
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  )
}
